/**
 * Screen — core.
 *
 * Photographs pinned to the depth they cover, the plug measurements taken from
 * them, and the crossplot that ties both back to the logs.
 *
 * The control that matters most on this screen is the core-to-log shift. Core
 * comes up with its own depth, measured off the drill string, and it disagrees
 * with the log by a few tens of centimetres in almost every well. Until that
 * shift is right, a calibration crossplot compares plugs against the wrong bed
 * and shows scatter that looks like bad core — so the shift is a slider here
 * with the crossplot beside it, and moving one redraws the other.
 */

import { useMemo, useState } from "react";
import { api, unpage, type Page } from "../api";
import { canWrite } from "../auth";
import { count } from "../lib/plural";
import { IntervalColumn } from "../components/StructuralTracks";
import {
  Button,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Modal,
  Notice,
  Panel,
  Segmented,
  Slider,
  useToast,
} from "../components/ui";
import { useAsync } from "../hooks";
import type {
  CalibrationPair,
  CoreCalibration,
  CoreDescription,
  CorePlug,
  CoreRun,
  Project,
  Well,
} from "../types";

const CURVE_CHOICES = [
  { value: "PHIE", label: "PHIE — effective porosity" },
  { value: "PHIT", label: "PHIT — total porosity" },
  { value: "PERM", label: "PERM — permeability" },
  { value: "SW", label: "SW — water saturation" },
];

export default function CoreScreen({
  project,
  well,
  onDataChanged,
}: {
  project: Project;
  well: Well;
  onDataChanged?: () => void;
}) {
  const toast = useToast();
  const mayWrite = canWrite(project.my_role);
  const [runId, setRunId] = useState<number | null>(null);
  const [curve, setCurve] = useState("PHIE");
  const [tab, setTab] = useState<"photo" | "plugs">("photo");
  const [dialog, setDialog] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [shiftDraft, setShiftDraft] = useState<number | null>(null);

  const runs = useAsync(
    (signal) => api
      .get<Page<CoreRun>>("/api/core-runs/", { well: well.id }, signal)
      .then((page) => unpage(page, signal)),
    [well.id, version],
  );

  const run = useMemo(
    () => runs.data?.find((row) => row.id === runId) ?? runs.data?.[0] ?? null,
    [runs.data, runId],
  );

  const plugs = useAsync(
    (signal) => api
      .get<Page<CorePlug>>("/api/core-plugs/", { well: well.id }, signal)
      .then((page) => unpage(page, signal)),
    [well.id, version],
  );

  const descriptions = useAsync(
    (signal) => api
      .get<Page<CoreDescription>>("/api/core-descriptions/", { well: well.id }, signal)
      .then((page) => unpage(page, signal)),
    [well.id, version],
  );

  const calibration = useAsync(
    (signal) => api.get<CoreCalibration>(
      `/api/wells/${well.id}/core-calibration/`, { curves: curve }, signal),
    [well.id, curve, version],
  );

  const shift = shiftDraft ?? run?.depth_shift ?? 0;

  const saveShift = async () => {
    if (!run || shiftDraft === null) return;
    try {
      await api.patch(`/api/core-runs/${run.id}/`, { depth_shift: shiftDraft });
      setShiftDraft(null);
      setVersion((v) => v + 1);
      toast("Core shift saved.");
      onDataChanged?.();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  if (runs.loading && !runs.data) return <Loading label="Membaca core…" />;
  if (!runs.data?.length) {
    return (
      <Empty>
        {well.name} has no core data yet.
        {mayWrite && (
          <Button variant="primary" onClick={() => setDialog("import-plugs")}>
            Import core analysis (CSV)
          </Button>
        )}
        {dialog === "import-plugs" && (
          <ImportPlugsDialog
            well={well}
            runs={[]}
            onClose={() => setDialog(null)}
            onDone={() => {
              setDialog(null);
              setVersion((v) => v + 1);
            }}
          />
        )}
      </Empty>
    );
  }

  const top = run?.top_md ?? 0;
  const base = run?.base_md ?? 1;
  const height = 560;
  const runPlugs = (plugs.data ?? []).filter(
    (plug) => plug.log_md >= top - 2 && plug.log_md <= base + 2);

  // Tabel mengikuti lintasan terpilih, dan di sini "mengikuti" berarti
  // keanggotaan, bukan kedalaman seperti pada kolom plug di sebelah foto.
  // Angka "Plug" yang berdiri tepat di sebelah tabel adalah `run.plugs.count()`
  // (serializers.py, get_plug_count), jadi penyaringan menurut jendela
  // kedalaman — yang di sana benar, sebab yang digambar adalah sumbu kedalaman
  // — akan menghitung dua kali plug di perbatasan dua lintasan dan tidak pernah
  // sepakat dengan angka di sebelahnya.
  const tablePlugs = (plugs.data ?? []).filter((plug) => plug.core_run === run?.id);
  // `core_run` adalah SET_NULL: menghapus sebuah lintasan sengaja menyisakan
  // plugnya hidup tanpa induk, sebab pengukurannya tetap sah meski catatan
  // lintasannya tidak ada lagi. Plug seperti itu bukan milik lintasan mana pun,
  // jadi tanpa keterangan ini ia lenyap dari satu-satunya tabel yang memuatnya
  // — persis kerugian diam yang penyaringan ini mestinya tidak menciptakan.
  const looseCount = (plugs.data ?? []).filter((plug) => plug.core_run == null).length;

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, overflow: "auto", padding: "10px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <Dropdown
            value={String(run?.id ?? "")}
            options={(runs.data ?? []).map((row) => ({
              value: String(row.id),
              label: `${row.name} · ${row.top_md.toFixed(0)}–${row.base_md.toFixed(0)} m`,
            }))}
            onChange={(value) => {
              setRunId(Number(value));
              setShiftDraft(null);
            }}
          />
          <Segmented
            value={tab}
            options={[
              { value: "photo", label: "Photograph" },
              { value: "plugs", label: "Plug table" },
            ]}
            onChange={setTab}
            size="sm"
          />
          <span style={{ flex: 1 }} />
          {mayWrite && (
            <Button variant="ghost" onClick={() => setDialog("import-plugs")}>
              Import plugs…
            </Button>
          )}
        </div>

        {tab === "photo" ? (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <DepthColumn top={top} base={base} height={height} />
            {run?.photos.length ? (
              run.photos.map((photo) => (
                <CorePhotoStrip
                  key={photo.id}
                  url={photo.url}
                  label={photo.light_label}
                  photoTop={photo.top_md}
                  photoBase={photo.base_md}
                  cropTop={photo.crop_top}
                  cropBase={photo.crop_base}
                  top={top}
                  base={base}
                  height={height}
                />
              ))
            ) : (
              <Notice tone="info">This run has no photograph yet.</Notice>
            )}

            <IntervalColumn
              intervals={(descriptions.data ?? [])
                .filter((row) => row.base_md >= top && row.top_md <= base)
                .map((row) => ({
                  top_md: row.top_md,
                  base_md: row.base_md,
                  color: row.color,
                  label: row.lithology.slice(0, 10),
                  hint: `${row.lithology} · ${row.grain_size} · ${row.structure}`,
                }))}
              width={92}
              scale={{ top, base, height }}
              title="Description"
            />

            <PlugColumn plugs={runPlugs} top={top} base={base} height={height} />
          </div>
        ) : (
          <PlugTable plugs={tablePlugs} looseCount={looseCount} />
        )}
      </div>

      <aside
        style={{
          width: 320,
          flex: "none",
          borderLeft: "1px solid var(--line)",
          background: "var(--panel)",
          overflow: "auto",
          padding: 8,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
        className="scroll-y"
      >
        <Panel title="Core run" meta={run?.name}>
          <KeyValue k="Interval" v={`${top.toFixed(1)}–${base.toFixed(1)} m`} />
          <KeyValue k="Recovery" v={run?.recovery != null ? `${(run.recovery * 100).toFixed(0)}%` : "—"} />
          <KeyValue k="Diameter" v={run?.diameter_mm ? `${run.diameter_mm.toFixed(1)} mm` : "—"} />
          <KeyValue k="Plugs" v={String(run?.plug_count ?? 0)} />
        </Panel>

        <Panel title="Core → log shift">
          <Slider
            label="Shift"
            value={shift}
            min={-3}
            max={3}
            step={0.05}
            onChange={setShiftDraft}
            format={(value) => `${value >= 0 ? "+" : ""}${value.toFixed(2)} m`}
          />
          <span style={{ font: "400 10.5px/1.6 var(--font-sans)", color: "var(--ink-5)" }}>
            Core depth is measured from the length of the drill string and almost never
            agrees with log depth. Slide until the cloud below is tightest — R² is the
            measure of that.
          </span>
          {shiftDraft !== null && shiftDraft !== run?.depth_shift && (
            <div style={{ display: "flex", gap: 6 }}>
              <Button variant="primary" disabled={!mayWrite} onClick={saveShift}>
                Save {shiftDraft >= 0 ? "+" : ""}{shiftDraft.toFixed(2)} m
              </Button>
              <Button variant="ghost" onClick={() => setShiftDraft(null)}>Cancel</Button>
            </div>
          )}
          {shiftDraft !== null && (
            <Notice tone="info">
              The cloud below still uses the saved shift
              ({(run?.depth_shift ?? 0).toFixed(2)} m) until the new one is saved.
            </Notice>
          )}
        </Panel>

        <Panel title="Core–log calibration">
          <Dropdown value={curve} options={CURVE_CHOICES} onChange={setCurve} />
          {calibration.loading && !calibration.data && <Loading />}
          {calibration.data && (
            <CalibrationPlot
              pairs={calibration.data.pairs[curve] ?? []}
              fit={calibration.data.regression[curve] ?? null}
              curve={curve}
            />
          )}
          {calibration.data && (
            <>
              <KeyValue k="Pairs" v={String((calibration.data.pairs[curve] ?? []).length)} />
              <KeyValue k="Plugs available" v={String(calibration.data.plug_count)} />
              <KeyValue
                k="R²"
                v={calibration.data.regression[curve]?.r2?.toFixed(3) ?? "—"}
              />
              <KeyValue
                k="core = a·log + b"
                v={
                  calibration.data.regression[curve]
                    ? `${calibration.data.regression[curve]!.slope.toFixed(3)} · log `
                      + `${calibration.data.regression[curve]!.intercept >= 0 ? "+" : "−"} `
                      + `${Math.abs(calibration.data.regression[curve]!.intercept).toFixed(4)}`
                    : "—"
                }
              />
              {(calibration.data.pairs[curve] ?? []).length === 0 && (
                <Notice tone="warn">
                  No pairs. Either the {curve} curve is not in this well yet, or the
                  shift has carried the plugs outside the recorded interval.
                </Notice>
              )}
            </>
          )}
        </Panel>
      </aside>

      {dialog === "import-plugs" && (
        <ImportPlugsDialog
          well={well}
          runs={runs.data ?? []}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setVersion((v) => v + 1);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function DepthColumn({ top, base, height }: { top: number; base: number; height: number }) {
  const span = base - top || 1;
  const step = span > 20 ? 5 : span > 8 ? 2 : 1;
  const marks: number[] = [];
  for (let md = Math.ceil(top / step) * step; md <= base; md += step) marks.push(md);
  return (
    <svg viewBox={`0 0 44 ${height}`} width={44} height={height} style={{ flex: "none" }}>
      {marks.map((md) => {
        const y = ((md - top) / span) * height;
        return (
          <g key={md}>
            <line x1={34} y1={y} x2={44} y2={y} stroke="var(--line-strong)" strokeWidth={0.7} />
            <text x={31} y={y + 3} textAnchor="end"
                  style={{ font: "400 9px var(--font-mono)", fill: "var(--ink-5)" }}>
              {md.toFixed(0)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * One photograph, stretched so that its cropped extent lands on its depths.
 *
 * The crop fractions matter: a core-box photograph almost always includes a
 * ruler, a label card and some table, and mapping the depth range across the
 * whole file puts bed boundaries on the label.
 */
function CorePhotoStrip({
  url, label, photoTop, photoBase, cropTop, cropBase, top, base, height,
}: {
  url: string | null;
  label: string;
  photoTop: number;
  photoBase: number;
  cropTop: number;
  cropBase: number;
  top: number;
  base: number;
  height: number;
}) {
  const span = base - top || 1;
  const y = ((photoTop - top) / span) * height;
  const stripHeight = Math.max(((photoBase - photoTop) / span) * height, 4);
  const cropSpan = Math.max(cropBase - cropTop, 0.01);

  return (
    <div style={{ width: 116, flex: "none" }}>
      <div style={{
        font: "500 9.5px var(--font-mono)",
        color: "var(--ink-4)",
        textAlign: "center",
        borderBottom: "1px solid var(--line)",
        padding: "2px 0",
      }}>
        {label}
      </div>
      <div style={{ position: "relative", height, background: "var(--panel-inset)" }}>
        {url && (
          <div style={{
            position: "absolute",
            top: y,
            left: 0,
            right: 0,
            height: stripHeight,
            overflow: "hidden",
          }}>
            <img
              src={url}
              alt={`Core ${photoTop.toFixed(1)}–${photoBase.toFixed(1)} m`}
              style={{
                position: "absolute",
                width: "100%",
                // Bagian yang berisi core direntangkan ke seluruh tinggi pita,
                // dan sisanya digeser keluar bingkai.
                height: `${(1 / cropSpan) * 100}%`,
                top: `${(-cropTop / cropSpan) * 100}%`,
                objectFit: "fill",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Plug depths as ticks, so the sampling density is visible beside the photo. */
function PlugColumn({
  plugs, top, base, height,
}: {
  plugs: CorePlug[];
  top: number;
  base: number;
  height: number;
}) {
  const span = base - top || 1;
  const maxPorosity = Math.max(0.35, ...plugs.map((plug) => plug.porosity ?? 0));
  return (
    <div style={{ width: 92, flex: "none" }}>
      <div style={{
        font: "500 9.5px var(--font-mono)",
        color: "var(--ink-4)",
        textAlign: "center",
        borderBottom: "1px solid var(--line)",
        padding: "2px 0",
      }}>
        Core φ
      </div>
      <svg viewBox={`0 0 92 ${height}`} width={92} height={height}
           style={{ background: "var(--panel-inset)" }}>
        {plugs.map((plug) => {
          const y = ((plug.log_md - top) / span) * height;
          const x = ((plug.porosity ?? 0) / maxPorosity) * 88;
          return (
            <g key={plug.id}>
              <line x1={0} y1={y} x2={x} y2={y}
                    stroke={plug.rejected ? "var(--ink-6)" : "#2e8b57"}
                    strokeWidth={1}
                    strokeDasharray={plug.rejected ? "2 2" : undefined} />
              <circle cx={x} cy={y} r={2}
                      fill={plug.rejected ? "none" : "#2e8b57"}
                      stroke="#2e8b57" strokeWidth={0.9} />
              <title>
                {plug.md.toFixed(2)} m core → {plug.log_md.toFixed(2)} m log ·
                {" "}φ {plug.porosity != null ? plug.porosity.toFixed(3) : "—"} ·
                {" "}k {plug.permeability != null ? `${plug.permeability.toFixed(1)} mD` : "—"}
                {plug.rejected && " · rejected"}
              </title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function PlugTable({ plugs, looseCount }: { plugs: CorePlug[]; looseCount: number }) {
  const loose = looseCount > 0 && (
    <Notice tone="info">
      {count(looseCount, "plug")} in this well belong to no run — the run was deleted
      while the measurements were kept. This table shows the selected run, so those plugs
      are not here.
    </Notice>
  );
  if (!plugs.length) {
    return (
      <>
        <Empty>This run has no plugs yet.</Empty>
        {loose}
      </>
    );
  }
  return (
    <div style={{ overflow: "auto", maxHeight: 620 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", font: "400 11px var(--font-mono)" }}>
        <thead>
          <tr>
            {["MD core", "MD log", "φ", "k (mD)", "ρg", "Sw", "Lithology", "Facies", ""].map((head) => (
              <th key={head} style={{
                textAlign: "left",
                padding: "4px 8px",
                borderBottom: "1px solid var(--line-strong)",
                color: "var(--ink-5)",
                font: "500 10px var(--font-mono)",
                position: "sticky",
                top: 0,
                background: "var(--panel)",
              }}>
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {plugs.map((plug) => (
            <tr key={plug.id} style={{ opacity: plug.rejected ? 0.5 : 1 }}>
              <td style={cell}>{plug.md.toFixed(2)}</td>
              <td style={cell}>{plug.log_md.toFixed(2)}</td>
              <td style={cell}>{plug.porosity != null ? plug.porosity.toFixed(3) : "—"}</td>
              <td style={cell}>{plug.permeability != null ? plug.permeability.toFixed(2) : "—"}</td>
              <td style={cell}>{plug.grain_density != null ? plug.grain_density.toFixed(3) : "—"}</td>
              <td style={cell}>{plug.water_saturation != null ? plug.water_saturation.toFixed(3) : "—"}</td>
              <td style={cell}>{plug.lithology || "—"}</td>
              <td style={cell}>{plug.facies || "—"}</td>
              <td style={cell}>{plug.rejected ? "rejected" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {loose}
    </div>
  );
}

const cell: React.CSSProperties = {
  padding: "3px 8px",
  borderBottom: "1px solid var(--line-soft)",
  color: "var(--ink-3)",
  whiteSpace: "nowrap",
};

/**
 * Core against log, with the least-squares line and the 1:1 line.
 *
 * Both lines, always. The regression says what the relation is; the 1:1 says
 * what it would be if the log needed no correction at all, and the gap between
 * them is the whole reason anybody cores a well twice.
 */
function CalibrationPlot({
  pairs, fit, curve,
}: {
  pairs: CalibrationPair[];
  fit: { slope: number; intercept: number; r2: number | null; count: number } | null;
  curve: string;
}) {
  const size = 250;
  const pad = 30;
  const plot = size - pad - 8;

  const values = pairs.flatMap((pair) => [pair.core, pair.log]).filter(Number.isFinite);
  const hi = values.length ? Math.max(...values) * 1.08 : 1;
  const lo = 0;
  const span = hi - lo || 1;
  const xOf = (value: number) => pad + ((value - lo) / span) * plot;
  const yOf = (value: number) => size - pad - ((value - lo) / span) * plot;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <rect x={pad} y={8} width={plot} height={plot} fill="var(--panel-inset)"
            stroke="var(--line)" strokeWidth={0.8} />
      <line x1={xOf(lo)} y1={yOf(lo)} x2={xOf(hi)} y2={yOf(hi)}
            stroke="var(--ink-6)" strokeWidth={1} strokeDasharray="4 3" />
      {fit && (
        <line
          x1={xOf(lo)}
          y1={yOf(fit.slope * lo + fit.intercept)}
          x2={xOf(hi)}
          y2={yOf(fit.slope * hi + fit.intercept)}
          stroke="var(--accent)"
          strokeWidth={1.6}
        />
      )}
      {pairs.map((pair) => (
        <circle key={pair.plug} cx={xOf(pair.log)} cy={yOf(pair.core)} r={2.6}
                fill="#2e8b57" opacity={0.8}>
          <title>{`${pair.md.toFixed(2)} m · log ${pair.log.toFixed(3)} · core ${pair.core.toFixed(3)}`}</title>
        </circle>
      ))}
      <text x={pad + plot / 2} y={size - 6} textAnchor="middle"
            style={{ font: "400 9px var(--font-mono)", fill: "var(--ink-6)" }}>
        log {curve}
      </text>
      <text x={9} y={16} style={{ font: "400 9px var(--font-mono)", fill: "var(--ink-6)" }}>
        core
      </text>
    </svg>
  );
}

function ImportPlugsDialog({
  well, runs, onClose, onDone,
}: {
  well: Well;
  runs: CoreRun[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [runId, setRunId] = useState<string>(runs[0] ? String(runs[0].id) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const form = new FormData();
    form.append("well", String(well.id));
    form.append("file", file);
    if (runId) form.append("core_run", runId);
    try {
      await api.upload("/api/core-plugs/upload/", form);
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Import core analysis"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!file} onClick={send}>Import</Button>
        </>
      }
    >
      <label style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        padding: "24px 16px",
        border: "1.5px dashed var(--border-control)",
        borderRadius: "var(--radius-panel)",
        background: "var(--panel-inset)",
        cursor: "pointer",
      }}>
        <span style={{ font: "500 12px var(--font-sans)", color: "var(--ink-2)" }}>
          {file ? file.name : "Choose a CSV file"}
        </span>
        <input type="file" accept=".csv,.txt,text/csv"
               onChange={(event) => setFile(event.target.files?.[0] ?? null)}
               style={{ display: "none" }} />
      </label>
      {runs.length > 0 && (
        <Dropdown
          label="Core run"
          value={runId}
          options={runs.map((row) => ({ value: String(row.id), label: row.name }))}
          onChange={setRunId}
        />
      )}
      {error && <Notice tone="error">{error}</Notice>}
      <Notice tone="info">
        A depth column is required — its heading may be <code>MD</code>, <code>DEPTH</code>
        or <code>KEDALAMAN</code>. Porosity and saturation are accepted as fractions or as
        percentages; both are normalised to fractions, so that one file writing 23.4 and
        another writing 0.234 do not become two clouds a hundredfold apart.
      </Notice>
    </Modal>
  );
}
